import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

/**
 * NetworkContext — global online/offline state + user-facing offline toasts.
 *
 * Works on:
 *  - Web:      listens to window online/offline events + navigator.onLine
 *  - Android/iOS (Capacitor): same events fire via Capacitor WebView
 *
 * Usage:
 *   const { isOnline, blockIfOffline } = useNetwork();
 *   const handleUnlock = async () => {
 *     if (blockIfOffline("Reconnect to unlock this chapter.")) return;
 *     // ... proceed
 *   };
 */

const NetworkContext = createContext(null);

export const NetworkProvider = ({ children }) => {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
      ? navigator.onLine
      : true
  );
  const [toast, setToast] = useState(null); // {msg, id}
  const [wasOffline, setWasOffline] = useState(false);
  const toastTimerRef = useRef(null);
  const bannerTimerRef = useRef(null);

  useEffect(() => {
    const onOnline = () => {
      setIsOnline(true);
      setWasOffline((prev) => {
        if (prev) {
          // Show "Back online" banner for 3s
          if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
          bannerTimerRef.current = setTimeout(() => setWasOffline(false), 3000);
          return true;
        }
        return false;
      });
    };
    const onOffline = () => {
      setIsOnline(false);
      setWasOffline(true);
      if (bannerTimerRef.current) { clearTimeout(bannerTimerRef.current); bannerTimerRef.current = null; }
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    };
  }, []);

  const showOfflineToast = useCallback((msg) => {
    const id = Date.now() + Math.random();
    setToast({ msg: msg || "You're offline. Reconnect to continue.", id });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setToast((t) => (t && t.id === id ? null : t));
    }, 3500);
  }, []);

  /**
   * Gate a user action: if offline, show a specific toast and return true (blocked).
   * Returns false when the action should proceed.
   */
  const blockIfOffline = useCallback((msg) => {
    const online = typeof navigator !== 'undefined' ? navigator.onLine !== false : true;
    if (!online) {
      showOfflineToast(msg);
      return true;
    }
    return false;
  }, [showOfflineToast]);

  const dismissToast = useCallback(() => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast(null);
  }, []);

  return (
    <NetworkContext.Provider value={{ isOnline, wasOffline, toast, showOfflineToast, blockIfOffline, dismissToast }}>
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = () => {
  const ctx = useContext(NetworkContext);
  // Graceful fallback if consumer is mounted outside provider (e.g. isolated test)
  if (!ctx) {
    return {
      isOnline: true,
      wasOffline: false,
      toast: null,
      showOfflineToast: () => {},
      blockIfOffline: () => false,
      dismissToast: () => {},
    };
  }
  return ctx;
};
